import { Typography } from '@mui/material';
import { t } from 'i18next';
import moment from 'moment';
import { useContext, useEffect, useState, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';

import { Banner, BoxFlex, BrowserOnly, Countdown, Loading } from '@woovi/ui';

import type { PaymentPixAutomaticRedirect_subscription$key } from './__generated__/PaymentPixAutomaticRedirect_subscription.graphql.ts';
import PaymentContext from '../context/PaymentContext.tsx';

type Props = {
  pixAutomatic: PaymentPixAutomaticRedirect_subscription$key;
};

const REDIRECT_TIMEOUT = 5000; // 5 seconds

const PaymentPixAutomaticRedirect = (props: Props): ReactNode => {
  const [isRedirecting, setIsRedirecting] = useState(false);

  const pixAutomatic =
    useFragment<PaymentPixAutomaticRedirect_subscription$key>(
      graphql`
        fragment PaymentPixAutomaticRedirect_subscription on PaymentSubscription {
          redirectUrl
        }
      `,
      props.pixAutomatic,
    );

  const { startRedirect } = useContext(PaymentContext);

  useEffect(() => {
    if (!pixAutomatic?.redirectUrl || isRedirecting) {
      return;
    }

    if (startRedirect) {
      setIsRedirecting(true);
      setTimeout(() => {
        if (pixAutomatic.redirectUrl) {
          window.open(pixAutomatic.redirectUrl, '_self');
        }
      }, REDIRECT_TIMEOUT);
    }
  }, [startRedirect]);

  const now = moment().tz('America/Sao_Paulo');
  const redirectStart = now.add(REDIRECT_TIMEOUT, 'milliseconds');

  if (!startRedirect || !pixAutomatic.redirectUrl) {
    return null;
  }

  return (
    <Banner variant='outline' color='primary'>
      <BoxFlex
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          width: '100%',
        }}
      >
        <BrowserOnly>
          <Countdown
            targetDate={redirectStart.toISOString()}
            fallback={<Loading size={20} />}
          />
        </BrowserOnly>
        <Typography
          variant='subtitle1'
          fontWeight='bold'
          align='center'
          sx={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
        >
          {t('You are being redirected to {{redirectUrl}}', {
            redirectUrl: pixAutomatic.redirectUrl,
          })}
        </Typography>
      </BoxFlex>
    </Banner>
  );
};

export default PaymentPixAutomaticRedirect;
